import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
} from '@ant-design/icons';
import { notification } from 'antd';
import React, { useCallback, useMemo } from 'react';

import { config } from '@app/config';
import { PREFIX } from '@app/constants';
import {
  DEFAULT_ERROR_DESCRIPTION,
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_WARNING,
  ERROR,
  FRONT_ERROR_PREFIX,
  FRONT_WARNING,
  INFO,
  SUCCESS,
  SUCCESS_MESSAGE,
  WARNING,
} from '@app/constants/alerts';
import { NotificationContext } from '@app/providers/NotificationProvider/NotificationContext';
import type { NNotificationProvider } from '@app/providers/NotificationProvider/NotificationProvider.types';
import { handleCatchError } from '@app/services';
import { serverStatus } from '@app/services/__global__/serverStatus';

import type { ReactNode } from 'react';

import '@app/providers/NotificationProvider/styles/NotificationProvider.scss';

const Context = React.createContext({ error: 'Описание ошибки' });
const BOTTOM_RIGHT = 'bottomRight';
const COMPACT_CLASS_NAME = 'notification-compact';

// Свой значок вместо стандартного antd: попросили вшить его в текст
// заголовка (.ant-notification-notice-message), а не держать отдельным
// абсолютно спозиционированным элементом antd — поэтому штатная иконка
// antd скрыта общим селектором по ICON_CLASS_NAME в NotificationProvider.scss,
// и значок здесь рендерится как первый узел прямо внутри message.
// ICON_CLASS_NAME добавляется КО ВСЕМ вызовам api ниже (через withIconClassName),
// а не только к компактным/тосту: у INFO className берётся из options и может
// быть не передан вовсе (showDevNotification, смена email в useUserProfile) —
// без общего класса в этих случаях штатная иконка antd не скрывалась бы, и
// поверх неё рисовался бы ещё и наш inline-значок (issue из ревью MR !4861).
const ICON_CLASS_NAME = 'notification-with-inline-icon';
const withIconClassName = (extra?: string) =>
  extra ? `${ICON_CLASS_NAME} ${extra}` : ICON_CLASS_NAME;

const TITLE_ICON_STYLE = { marginInlineEnd: 4, verticalAlign: -1 };
const titleIcon = (Icon: typeof InfoCircleFilled, color: string) => (
  <Icon style={{ ...TITLE_ICON_STYLE, color }} />
);

const handleFirstSymbolInLowerCase = (errorText: string): string => {
  const firstSymbolInLowerCase = errorText.charAt(0).toLowerCase();
  const errorRest = errorText.slice(1);

  return firstSymbolInLowerCase + errorRest;
};

export const NotificationProvider: NNotificationProvider.Main = ({
  children,
}) => {
  const [api, contextHolder] = notification.useNotification();

  const openNotification: NNotificationProvider.OpenNotification = useCallback(
    (alert, type, description, options) => {
      const handleMessage = (alert: string | Error): string => {
        const isStringError = typeof alert === 'string';
        const isSuccess = type === SUCCESS;
        const emptyMessage = '';

        if (isStringError) {
          if (isSuccess) {
            return alert || SUCCESS_MESSAGE;
          }

          return alert;
        }

        const isObjectError = 'message' in alert;

        if (isObjectError) {
          return alert.message;
        }

        return emptyMessage;
      };

      const message = handleMessage(alert);

      const customMessage = (
        <span data-testid={`${PREFIX}notification-message`}>{message}</span>
      );

      const customDescription = description ? (
        <span data-testid={`${PREFIX}notification-description`}>
          {description}
        </span>
      ) : (
        ''
      );

      switch (type) {
        case SUCCESS:
          return api[type]({
            message: (
              <>
                {titleIcon(CheckCircleFilled, '#52c41a')}
                {customMessage}
              </>
            ),
            placement: BOTTOM_RIGHT,
            className: withIconClassName(COMPACT_CLASS_NAME),
          });
        case WARNING:
          return api[type]({
            message: (
              <>
                {titleIcon(ExclamationCircleFilled, '#faad14')}
                {DEFAULT_WARNING}
              </>
            ),
            description: customMessage,
            placement: BOTTOM_RIGHT,
            className: withIconClassName(COMPACT_CLASS_NAME),
          });
        case FRONT_WARNING:
          return api[WARNING]({
            message: (
              <>
                {titleIcon(ExclamationCircleFilled, '#faad14')}
                {DEFAULT_WARNING}
              </>
            ),
            description: (
              <span data-testid={`${PREFIX}notification-description`}>
                {/* «(FE)» — маркер фронтовой ошибки для QA на тестовых
                    стендах; клиенту на проде не показывается (task/10727) */}
                {config.mode === 'production'
                  ? message
                  : FRONT_ERROR_PREFIX + handleFirstSymbolInLowerCase(message)}
              </span>
            ),
            placement: BOTTOM_RIGHT,
            className: withIconClassName(COMPACT_CLASS_NAME),
          });
        case INFO:
          return api[type]({
            key: options?.key,
            className: withIconClassName(options?.className),
            message: (
              <>
                {titleIcon(InfoCircleFilled, '#1677ff')}
                {customMessage}
              </>
            ),
            description: customDescription,
            placement: options?.placement ?? 'top',
            duration: options?.duration ?? 0,
          });
        case ERROR:
        default:
          if (message) {
            return api[WARNING]({
              message: (
                <>
                  {titleIcon(ExclamationCircleFilled, '#faad14')}
                  {customMessage}
                </>
              ),
              placement: BOTTOM_RIGHT,
              className: withIconClassName(COMPACT_CLASS_NAME),
            });
          }

          return api.error({
            message: (
              <span data-testid={`${PREFIX}notification-message`}>
                {titleIcon(CloseCircleFilled, '#ff4d4f')}
                {DEFAULT_ERROR_MESSAGE}
              </span>
            ),
            description: (
              <Context.Consumer>
                {() => (
                  <span data-testid={`${PREFIX}notification-description`}>
                    {DEFAULT_ERROR_DESCRIPTION}
                  </span>
                )}
              </Context.Consumer>
            ),
            placement: BOTTOM_RIGHT,
            duration: 40,
            className: withIconClassName(COMPACT_CLASS_NAME),
          });
      }
    },
    [api]
  );

  const showNotification: NNotificationProvider.ShowNotificationType =
    useCallback(
      (
        notify: unknown,
        type: NNotificationProvider.NotifyType = 'warning',
        description?: ReactNode,
        options?: NNotificationProvider.NotifyOptions
      ) => {
        if (type === SUCCESS) {
          return;
        }

        if (serverStatus.isDown && type !== INFO) {
          return;
        }

        if (
          notify &&
          typeof notify === 'object' &&
          'status' in notify &&
          (notify as { status: string }).status === 'MAINTENANCE'
        ) {
          return;
        }

        const isAbortError =
          notify != null &&
          typeof notify === 'object' &&
          (('name' in notify &&
            (notify as { name: unknown }).name === 'AbortError') ||
            ('message' in notify &&
              (notify as { message: unknown }).message === 'Aborted'));

        if (isAbortError) {
          return;
        }

        const alert =
          typeof notify === 'string' ? notify : handleCatchError(notify);

        openNotification(alert, type, description, options);
      },
      [openNotification]
    );

  const contextValue = useMemo(() => ({ error: 'Описание ошибки' }), []);

  return (
    <NotificationContext.Provider
      value={{
        api,
        contextHolder,
        showNotification,
      }}
    >
      <Context.Provider value={contextValue}>
        {contextHolder}
        {children}
      </Context.Provider>
    </NotificationContext.Provider>
  );
};
